import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {useTranslation} from 'i18n'
import {ModalPolicyUpdateHandler} from 'types'
import {
  setModalPolicyUpdateHandler,
  showSnackbar,
  useAccountStatusHooks,
} from 'utils'
import {useDispatch} from 'lib/redux'
import ModalPolicyUpdateItem from './ModalPolicyUpdateItem'
import {ModalPolicyUpdateContext} from './ModalPolicyUpdateContext'

interface ModalPolicyUpdateProviderProps {
  children: ReactNode
}
export default function ModalPolicyUpdateProvider({
  children,
}: ModalPolicyUpdateProviderProps) {
  const {translate} = useTranslation()
  const {update} = useDispatch()
  const [visible, setVisible] = useState(false)
  const {handleAcceptPolicyUpdate} = useAccountStatusHooks()

  const handleShowModal = useCallback(() => {
    setVisible(true)
  }, [])

  const handleHideModal = useCallback(() => {
    setVisible(false)
  }, [])

  const handleAgree = useCallback(async () => {
    const response = await handleAcceptPolicyUpdate()
    handleHideModal()
    if (response !== 200) {
      showSnackbar(
        translate('global:messageError', {context: `code-${response}`}),
      )
    } else {
      update('user', {is_policy_accepted: true})
      update('generalAccountState', {triggerRefetch: true})
    }
  }, [handleAcceptPolicyUpdate, handleHideModal, translate, update])

  const handleRenderModal = useMemo(
    () => <ModalPolicyUpdateItem visible={visible} onAgree={handleAgree} />,
    [handleAgree, visible],
  )

  const handler = useMemo<ModalPolicyUpdateHandler>(
    () => ({
      showModal: handleShowModal,
    }),
    [handleShowModal],
  )

  useEffect(() => setModalPolicyUpdateHandler(handler), [handler])

  return (
    <ModalPolicyUpdateContext.Provider value={handler}>
      {handleRenderModal}
      {children}
    </ModalPolicyUpdateContext.Provider>
  )
}
